import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IApplicationTag from '../../interfaces/IApplicationTag'
import IApplicationTagShelter from '../../interfaces/IApplicationTagShelter'
import IApplicationTagEventShelter from '../../interfaces/IApplicationTagEventShelter'
import {
  SearchRequest,
  ApplicationTagEnableRequest,
  ApplicationTagShelterRequest,
  ApplicationTagShelterSaveRequest,
  ApplicationTagEventRequest,
  ApplicationTagEventSaveRequest
} from '../../services/api/interfaces/ApiRequest'
import {
  applicationTagEnable,
  applicationTagEventList,
  applicationTagEventSave,
  applicationTagList,
  applicationTagShelterList,
  applicationTagShelterSave,
} from '../../services/api'

interface ApplicationTagContextState extends BaseContextState {
  applicationTags: IApplicationTag[] | null,
  shelterTags: IApplicationTagShelter[] | null,
  shelterEvents: IApplicationTagEventShelter[] | null
}

export interface ApplicationTagContextData extends ApplicationTagContextState {
  resetErrors: Function,
  requestApplicationTagList: (args: SearchRequest) => Promise<any>,
  requestApplicationTagEnable: (args: ApplicationTagEnableRequest) => Promise<any>,
  requestShelterTagList: (payload: ApplicationTagShelterRequest) => Promise<any>,
  requestShelterTagSave: (payload: ApplicationTagShelterSaveRequest) => Promise<any>,
  requestShelterEventList: (payload: ApplicationTagEventRequest) => Promise<any>,
  requestShelterEventSave: (payload: ApplicationTagEventSaveRequest) => Promise<any>,
}

// Create context
const { Provider, Consumer } = createContext({} as ApplicationTagContextData)

// Export consumer
export const ApplicationTagConsumer = Consumer

export class ApplicationTagProvider extends BaseContextProvider {
  state: ApplicationTagContextState = {
    applicationTags: null,
    shelterTags: null,
    shelterEvents: null
  }

  /**
   * Make api request for application tags list
   * 
   * @param {SearchRequest} payload 
   */
   requestApplicationTagList = (payload: SearchRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await applicationTagList(payload)
        this.processResponse(response, {
          paginate: {
            destination: 'applicationTags'
          }
        })
  
        if (this.state.success) {
          resolve(this.state.applicationTags)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter tags list
   * 
   * @param {ApplicationTagShelterRequest} payload 
   */
   requestShelterTagList = (payload: ApplicationTagShelterRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await applicationTagShelterList(payload)
        this.processResponse(response, {
          mappings: {
            origin: 'shelter_tags', destination: 'shelterTags'
          }
        })
  
        if (this.state.success) {
          resolve(this.state.shelterTags)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter tags save
   * 
   * @param {ApplicationTagShelterSaveRequest} payload 
   */
   requestShelterTagSave = (payload: ApplicationTagShelterSaveRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await applicationTagShelterSave(payload)
        this.processResponse(response, {
          mappings: {
            origin: 'shelter_tags', destination: 'shelterTags'
          }
        })
  
        if (this.state.success) {
          resolve(this.state.shelterTags)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter events list
   * 
   * @param {ApplicationTagEventRequest} payload 
   */
   requestShelterEventList = (payload: ApplicationTagEventRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await applicationTagEventList(payload)
        this.processResponse(response, {
          mappings: {
            origin: 'shelter_events', destination: 'shelterEvents'
          }
        })
  
        if (this.state.success) {
          resolve(this.state.shelterEvents)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter events save
   * 
   * @param {ApplicationTagEventSaveRequest} payload 
   */
   requestShelterEventSave = (payload: ApplicationTagEventSaveRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await applicationTagEventSave(payload)
        this.processResponse(response, {
          mappings: {
            origin: 'shelter_events', destination: 'shelterEvents'
          }
        })
  
        if (this.state.success) {
          resolve(this.state.shelterEvents)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for enable process tag
   * 
   * @param {ApplicationTagEnableRequest} payload 
   */
   requestApplicationTagEnable = (payload: ApplicationTagEnableRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await applicationTagEnable(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestApplicationTagList: this.requestApplicationTagList,
          requestApplicationTagEnable: this.requestApplicationTagEnable,
          requestShelterTagList: this.requestShelterTagList,
          requestShelterTagSave: this.requestShelterTagSave,
          requestShelterEventList: this.requestShelterEventList,
          requestShelterEventSave: this.requestShelterEventSave,
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
